import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import {
    CONVERSATION_API,
    type loader,
} from '~/routes/remix-api.chat.conversation';
import {
    CREATE_CONVERSATION_API,
    type action,
} from '~/routes/remix-api.chat.create-conversation';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation, Message } from '../types';
import { createOptimisticMessage } from '../utils';
import { useConversationCache } from './use-conversation-cache';
import { useRealTimeChat } from './use-real-time-chat';

/**
 * Hook to handle creating new conversations.
 */
export function useCreateConversation({
    participantIDs,
}: {
    /**
     * The IDs of the participants of the conversation to create. If this array is
     * empty, this hook is disabled and the preview conversation will not be fetched.
     */
    participantIDs: number[];
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { getCachedConversationByParticipants, cacheConversation } =
        useConversationCache();
    const fetcher = useFetcher<typeof loader>();
    const createFetcher = useFetcher<typeof action>();
    const [conversation, setConversation] = useState<Conversation | null>(null);
    const [optimisticMessage, setOptimisticMessage] = useState<Message | null>(
        null
    );
    const isLoading = fetcher.state !== 'idle';
    const createdConversation = useMemo(() => {
        return createFetcher.data?.conversation ?? null;
    }, [createFetcher.data]);
    const apiUrl = useMemo(() => {
        const url = new URL(CONVERSATION_API, window.location.origin);
        for (const participantID of participantIDs) {
            url.searchParams.append(
                'participantUserIDs',
                String(participantID)
            );
        }
        return `${url.pathname}${url.search}`;
    }, [participantIDs]);

    // Update the conversation preview when a new message is received.
    useRealTimeChat({
        onMessage: (message) => {
            if (String(message.conversation_id) === conversation?.ID) {
                fetcher.load(apiUrl);
            }
        },
    });

    const createConversation = useCallback(
        (message?: string, attachments?: File[]) => {
            if (participantIDs.length === 0 || isLoading || !isLoggedIn) return;
            const formData = new FormData();
            for (const participantID of participantIDs) {
                formData.append('memberIDs', String(participantID));
            }
            if (message) {
                formData.append('message', message);
            }
            if (attachments?.length) {
                attachments.forEach((attachment) => {
                    formData.append('attachments', attachment);
                });
            }
            createFetcher.submit(formData, {
                action: CREATE_CONVERSATION_API,
                method: 'POST',
                encType: attachments?.length
                    ? 'multipart/form-data'
                    : undefined,
            });
            setOptimisticMessage(
                createOptimisticMessage({
                    message,
                    attachments,
                    conversation,
                    user,
                })
            );
        },
        [
            participantIDs,
            isLoading,
            isLoggedIn,
            user,
            conversation,
            createFetcher,
        ]
    );

    // Fetch the conversation details from the participants when the participant IDs change.
    useEffect(() => {
        if (participantIDs.length === 0) return;
        fetcher.load(apiUrl);
        // Must not include `fetcher` or `participantIDs` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // Set the conversation preview object when loaded.
    useEffect(() => {
        // Reset the conversation preview when the participant IDs change.
        setConversation((prev) => {
            if (!prev || participantIDs.length === 0) {
                return null;
            }
            const isParticipantsMatch =
                prev.Participants.length === participantIDs.length + 1 &&
                participantIDs.every((participantID) =>
                    prev.Participants.some(
                        ({ Identity }) =>
                            Identity.__typename === 'Member' &&
                            Identity.ID === participantID
                    )
                );
            if (isParticipantsMatch) {
                return prev;
            }
            return null;
        });
        if (fetcher.data && fetcher.state === 'idle') {
            setConversation(fetcher.data.conversation);
            if (fetcher.data.conversation) {
                cacheConversation(fetcher.data.conversation);
            }
        }
        // Reset fetcher.data to not show the previous loaded conversation and
        // avoid infinite loop when setting the conversation (cache).
        if (fetcher.data) {
            fetcher.data = undefined;
        }
    }, [participantIDs, fetcher, cacheConversation]);

    // Use the cached conversation if it exists.
    useEffect(() => {
        if (participantIDs.length === 0) return;
        const cachedConversation = getCachedConversationByParticipants(
            user.ID,
            participantIDs
        );
        if (cachedConversation) {
            setConversation(cachedConversation);
        }
    }, [participantIDs, user.ID, getCachedConversationByParticipants]);

    // Cache the created conversation.
    useEffect(() => {
        if (createdConversation) {
            cacheConversation(createdConversation);
        }
    }, [createdConversation, cacheConversation]);

    return {
        /**
         * The conversation containing the participants in the props. If the
         * conversation with the given participants does not exist, this will
         * be `null`.
         */
        conversation,
        /**
         * The conversation that was created by the `createConversation` function.
         * This is `null` if not created yet.
         */
        createdConversation,
        /**
         * The message object that was created by the `createConversation` function.
         * This can be used for optimistic UI.
         */
        optimisticMessage,
        /**
         * The conversation is currently being loaded.
         */
        isLoading,
        /**
         * The conversation is currently being created.
         */
        isSubmitting: createFetcher.state === 'submitting',
        /**
         * Create a new conversation.
         */
        createConversation,
    };
}
